import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { usePlatformAdminAuth } from '../contexts/PlatformAdminAuthContext';

/**
 * Platform Admin Side Navigation
 * 
 * Navigation sidebar for platform admin portal with sections:
 * - System Configuration
 * - ML Infrastructure
 * - Tenant Management
 * - Data Governance
 * - Platform Monitoring
 */

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactElement;
  tooltip?: string;
  requireSuperAdmin?: boolean;
  children?: {
    label: string;
    path: string;
    tooltip?: string;
    requireSuperAdmin?: boolean;
  }[];
}

const navItems: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/platform-admin/dashboard',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    tooltip: 'Platform overview and system health',
  },
  {
    label: 'System Configuration',
    path: '/platform-admin/system',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    tooltip: 'Platform-wide configuration settings',
    requireSuperAdmin: true,
    children: [
      { label: 'Platform Settings', path: '/platform-admin/system/platform-settings', tooltip: 'Configure platform-wide settings', requireSuperAdmin: true },
      { label: 'Alert Thresholds', path: '/platform-admin/system/alert-thresholds', tooltip: 'Configure ML alert thresholds', requireSuperAdmin: true },
      { label: 'Feature Flags', path: '/platform-admin/system/feature-flags', tooltip: 'Manage feature rollouts', requireSuperAdmin: true },
      { label: 'Threat Intel Feeds', path: '/platform-admin/system/threat-intel-feeds', tooltip: 'Manage threat intelligence feeds', requireSuperAdmin: true },
    ],
  },
  {
    label: 'ML Infrastructure',
    path: '/platform-admin/ml',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    tooltip: 'Machine learning infrastructure management',
    children: [
      { label: 'Model Settings & MLflow', path: '/platform-admin/ml/models', tooltip: 'Configure models with MLflow experiment tracking' },
      { label: 'Model Feature Flags', path: '/platform-admin/ml/feature-flags', tooltip: 'Enable/disable ML models per tenant for compliance and cost optimization' },
      { label: 'Model Snapshots', path: '/platform-admin/ml/snapshots', tooltip: 'View and manage model snapshots for rollback and audit purposes' },
      { label: 'Training Pipeline', path: '/platform-admin/ml/training', tooltip: 'Monitor model training jobs and pipeline health' },
      { label: 'Dataset Management', path: '/platform-admin/ml/datasets', tooltip: 'Browse, upload, and manage training datasets in S3' },
      { label: 'Inference Monitoring', path: '/platform-admin/ml/inference', tooltip: 'Real-time inference performance and model health' },
      { label: 'Calibration Monitor', path: '/platform-admin/ml/calibration', tooltip: 'Monitor model calibration quality (ECE/MCE/Brier scores)' },
      { label: 'Enrichment Pipeline', path: '/platform-admin/ml/enrichment', tooltip: 'Alert enrichment pipeline health and performance metrics' },
      { label: 'Auto-Retraining Jobs', path: '/platform-admin/ml/retraining', tooltip: 'Monitor autonomous retraining job status and triggers' },
      { label: 'Real-Time Performance', path: '/platform-admin/ml/performance', tooltip: 'Live system performance metrics and optimization' },
      { label: 'Model Explainability', path: '/platform-admin/ml/explainability', tooltip: 'SHAP visualizations and model decision explanations' },
      { label: 'Feedback Analytics', path: '/platform-admin/ml/feedback', tooltip: 'Cross-tenant feedback patterns and sources' },
      { label: 'Feedback Sources', path: '/platform-admin/ml/sources', tooltip: 'All feedback sources across tenants' },
      { label: 'Hyperparameter Tuning', path: '/platform-admin/ml/tuning', tooltip: 'ML model hyperparameter optimization' },
    ],
  },
  {
    label: 'Tenant Management',
    path: '/platform-admin/tenants',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    tooltip: 'Manage all tenant organizations',
    children: [
      { label: 'All Tenants', path: '/platform-admin/tenants/list', tooltip: 'View and manage all tenants' },
      { label: 'Create Tenant', path: '/platform-admin/tenants/create', tooltip: 'Create new tenant with wizard' },
      { label: 'Tenant Health', path: '/platform-admin/tenants/health', tooltip: 'Monitor tenant system health' },
      { label: 'Usage Analytics', path: '/platform-admin/tenants/usage', tooltip: 'Tenant resource usage' },
      { label: 'Onboarding', path: '/platform-admin/tenants/onboarding', tooltip: 'New tenant provisioning' },
    ],
  },
  {
    label: 'Data Governance',
    path: '/platform-admin/data',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    tooltip: 'Data lifecycle and compliance',
    children: [
      { label: 'Retention Policies', path: '/platform-admin/data/retention', tooltip: 'Configure data retention rules' },
      { label: 'Data Classification', path: '/platform-admin/data/classification', tooltip: 'Manage data classification' },
      { label: 'Audit Logs', path: '/platform-admin/data/audit-logs', tooltip: 'Platform-wide audit trail' },
      { label: 'Compliance Reports', path: '/platform-admin/data/compliance', tooltip: 'Generate compliance reports' },
    ],
  },
  {
    label: 'Platform Monitoring',
    path: '/platform-admin/monitoring',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    tooltip: 'System health and performance',
    children: [
      { label: 'System Health', path: '/platform-admin/monitoring/health', tooltip: 'Overall system health status' },
      { label: 'Infrastructure Metrics', path: '/platform-admin/monitoring/infrastructure', tooltip: 'AWS infrastructure metrics' },
      { label: 'API Performance', path: '/platform-admin/monitoring/api', tooltip: 'API response times and errors' },
      { label: 'Database Performance', path: '/platform-admin/monitoring/database', tooltip: 'Database query performance' },
      { label: 'Log Explorer', path: '/platform-admin/monitoring/logs', tooltip: 'System-wide log analysis and monitoring' },
    ],
  },
  {
    label: 'Platform Admins',
    path: '/platform-admin/admins',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    tooltip: 'Manage platform administrators',
    requireSuperAdmin: true,
    children: [
      { label: 'Admin Users', path: '/platform-admin/admins/users', tooltip: 'Manage platform admin accounts', requireSuperAdmin: true },
      { label: 'Permissions', path: '/platform-admin/admins/permissions', tooltip: 'Configure admin permissions', requireSuperAdmin: true },
      { label: 'Activity Log', path: '/platform-admin/admins/activity', tooltip: 'Platform admin activity audit', requireSuperAdmin: true },
    ],
  },
];

interface PlatformAdminSideNavProps {
  isOpen: boolean;
}

export const PlatformAdminSideNav: React.FC<PlatformAdminSideNavProps> = ({ isOpen }) => {
  const location = useLocation();
  const { platformAdmin } = usePlatformAdminAuth();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

  React.useEffect(() => {
    // Auto-expand active parent items
    const activeParent = navItems.find(item => 
      location.pathname.startsWith(item.path)
    );
    
    if (activeParent) {
      setExpandedItems(prev => 
        prev.includes(activeParent.path) ? prev : [...prev, activeParent.path]
      );
    }
  }, [location]);

  const toggleItem = (path: string) => {
    setExpandedItems(prev => 
      prev.includes(path) 
        ? prev.filter(p => p !== path)
        : [...prev, path]
    );
  };

  // Filter items based on super admin requirement
  const filteredNavItems = navItems.filter(item => {
    if (item.requireSuperAdmin && !platformAdmin?.is_super_admin) {
      return false;
    }
    return true;
  });

  return (
    <nav className={`
      fixed left-0 top-16 h-[calc(100vh-64px)] w-64 bg-gray-900 text-white shadow-lg overflow-y-auto border-r border-gray-800 z-40
      transition-transform duration-300 ease-in-out
      ${
        isOpen 
          ? 'translate-x-0' 
          : '-translate-x-full'
      }
    `}>
      <div className="py-4">
        {filteredNavItems.map((item) => (
          <div key={item.path} className="mb-2">
            {item.children ? (
              <>
                <button
                  onClick={() => toggleItem(item.path)}
                  className={`flex items-center w-full px-4 py-2 text-left hover:bg-gray-800 transition-colors ${
                    location.pathname.includes(item.path) ? 'bg-gray-800 text-purple-400' : ''
                  }`}
                  aria-expanded={expandedItems.includes(item.path)}
                  title={item.tooltip}
                >
                  <span className="mr-3">{item.icon}</span>
                  <span className="flex-grow text-sm font-medium">{item.label}</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${
                      expandedItems.includes(item.path) ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {expandedItems.includes(item.path) && item.children && (
                  <div className="mt-1 ml-6 space-y-1">
                    {item.children
                      .filter(child => !child.requireSuperAdmin || platformAdmin?.is_super_admin)
                      .map((child) => (
                        <NavLink
                          key={child.path}
                          to={child.path}
                          className={({ isActive }) =>
                            `block px-4 py-2 text-sm hover:bg-gray-800 transition-colors ${
                              isActive ? 'bg-gray-800 text-purple-400 font-medium' : 'text-gray-300'
                            }`
                          }
                          title={child.tooltip}
                        >
                          {child.label}
                        </NavLink>
                      ))}
                  </div>
                )}
              </>
            ) : (
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center px-4 py-2 hover:bg-gray-800 transition-colors ${
                    isActive ? 'bg-gray-800 text-purple-400' : ''
                  }`
                }
                title={item.tooltip}
              >
                <span className="mr-3">{item.icon}</span>
                <span className="text-sm font-medium">{item.label}</span>
              </NavLink>
            )}
          </div>
        ))}
      </div>
    </nav>
  );
};

export default PlatformAdminSideNav;
